import apis from './apis';
import type { TFunction } from 'i18next';

/**
 * Branded PDF reports (F-REPORTS): My time and Firm economics.
 *
 * The server builds the PDF (firm letterhead, totals, tables); the labels are
 * sent from here so the report is in the user's language. Small files, so the
 * app's Axios instance with a blob response is fine - same as the matter
 * exports.
 */

export interface ReportRange {
  date_from?: string;
  date_to?: string;
}

const reportLabels = (t: TFunction): Record<string, string> => ({
  my_time_title: t('My time'),
  economics_title: t('Firm economics'),
  period: t('Period'),
  all_time: t('All time'),
  person: t('Person'),
  generated: t('Generated'),
  page: t('Page'),
  empty: t('Nothing in this period.'),
  total_hours: t('Total hours'),
  billable_hours: t('Billable hours'),
  non_billable_hours: t('Non-billable hours'),
  entries: t('Entries'),
  by_matter: t('By matter'),
  by_activity: t('By activity'),
  all_entries: t('All entries'),
  date: t('Date'),
  matter: t('Matter'),
  activity: t('Activity'),
  description: t('Description'),
  hours: t('Hours'),
  billable: t('Billable'),
  yes: t('Yes'),
  no: t('No'),
  total: t('Total'),
  utilisation: t('Utilisation'),
  revenue: t('Revenue'),
  by_person: t('By person'),
  by_client: t('By client'),
  client: t('Client'),
  matters: t('Matters'),
  untitled: t('Untitled'),
  unassigned: t('Unassigned'),
});

async function downloadPdf(url: string, range: ReportRange, t: TFunction, filename: string): Promise<void> {
  const res = await apis.post(url, { ...range, labels: reportLabels(t) }, { responseType: 'blob' });
  const blob = new Blob([res.data], { type: 'application/pdf' });
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
}

const suffix = (range: ReportRange) =>
  range.date_from || range.date_to ? `-${range.date_from || ''}_${range.date_to || ''}` : '';

export const downloadMyTimeReport = (range: ReportRange, t: TFunction) =>
  downloadPdf('/users/me/time-report', range, t, `${t('My time')}${suffix(range)}.pdf`);

export const downloadEconomicsReport = (range: ReportRange, t: TFunction) =>
  downloadPdf('/organizations/me/economics/report', range, t, `${t('Firm economics')}${suffix(range)}.pdf`);
